/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Michael Diponio
 * Created: 23/05/2022
 */
import * as React from 'react';
import { renderToString } from 'react-dom/server';

import { PDFData } from '@/wpt-lib/pdf/useData';
import { getBreakdownChartData } from '@/wpt-lib/components/Charts/getBreakdownChartData';
import PieChart from '@/wpt-lib/pdf/charts/PieChart';
import BarChart from '@/wpt-lib/pdf/charts/BarChart';
import getWorkloadChartData from '@/wpt-lib/components/Charts/getWorkloadChartData';
import { Labels } from '@/wpt-lib/components/contexts/LabelsContext/LabelsProvider';

/* Factor to scale size for sharpness. */
const ScaleFactor = 2;

export async function makeBreakdownChart(data: PDFData): Promise<JSX.Element> {
    const breakdownChartData = getBreakdownChartData(
        data.version,
        data.summary.counts,
        data.labels['academicFocusArea'] as Labels['ACTIVITY']['academicFocusArea'],
    );

    return <PieChart data={breakdownChartData} />;
}

export async function makeAllocationChart(data: PDFData): Promise<JSX.Element> {
    const allocationData = getWorkloadChartData(
        data.version,
        data.labels['academicFocusArea'] as Labels['ACTIVITY']['academicFocusArea'],
    );

    const size = {
        width: 380,
        height: 240,
    };

    return <BarChart data={allocationData} unit="%" {...size} />;
}

export function renderSVG(
    element: React.ReactElement,
    chartSize: {
        width: number;
        height: number;
    },
): Promise<string> {
    return new Promise((resolve, reject) => {
        /* Renders the chart to SVG markup that can be drawn in an image tag. */
        let svg = renderToString(element);

        /* For some reason the produced SVG XML does not have the SVG
         * namespaces attached and the browser refuses to render the SVG
         * without them. */
        svg = svg.replace('<svg', '<svg xmlns:svg="http://www.w3.org/2000/svg" xmlns="http://www.w3.org/2000/svg"');

        const blob = new Blob([svg], { type: 'image/svg+xml' });
        const imgURL = URL.createObjectURL(blob);

        /* Load image then draw to canvas. */
        const image = new Image();
        image.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = chartSize.width * ScaleFactor;
            canvas.height = chartSize.height * ScaleFactor;

            const ctx = canvas.getContext('2d');
            if (!ctx) {
                reject('Failed to load canvas context');
                return;
            }

            ctx.drawImage(image, 0, 0, chartSize.width * ScaleFactor, chartSize.height * ScaleFactor);

            URL.revokeObjectURL(imgURL);
            resolve(canvas.toDataURL());
        };

        image.onerror = (e) => {
            console.log('Error loading SVG image.', e);
            reject('Error loading SVG');
        };

        image.src = imgURL;
    });
}
